import type { FC, PointerEvent, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'

interface TiltProps {
    children: ReactNode
    max?: number
    className?: string
}

/**
 * Leans its content toward the mouse. Ignores touch and pen input.
 */
export const Tilt: FC<TiltProps> = ({ children, max = 7, className }) => {
    const ref = useRef<HTMLDivElement>(null)

    const onMove = (event: PointerEvent<HTMLDivElement>) => {
        const node = ref.current

        if (!node || event.pointerType !== 'mouse') return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        const rect = node.getBoundingClientRect()
        const x = (event.clientX - rect.left) / rect.width - 0.5
        const y = (event.clientY - rect.top) / rect.height - 0.5

        node.style.transform = `perspective(1200px) rotateX(${(y * max).toFixed(2)}deg) rotateY(${(-x * max).toFixed(2)}deg) translateZ(0)`
        node.style.setProperty('--glare-x', `${((x + 0.5) * 100).toFixed(1)}%`)
        node.style.setProperty('--glare-y', `${((y + 0.5) * 100).toFixed(1)}%`)
    }

    const onLeave = () => {
        const node = ref.current

        if (node) node.style.transform = ''
    }

    return (
        <div
            ref={ref}
            onPointerMove={onMove}
            onPointerLeave={onLeave}
            className={cn('transition-transform duration-500 ease-expo will-change-transform', className)}
        >
            {children}
        </div>
    )
}
